import { createSelector } from 'reselect';

import type { MayBe } from 'modules/global/typing/common/common';
import { LeadStatusEnum } from 'modules/global/typing/enums/leads.enum';
import type { StoredLead } from 'modules/leads/types';
import { getCurrentUserIsSalesman, hasSalesManagingRole, isCurrentUserSalesManager } from 'modules/users/selectors/currentUser';
import type { RootState } from 'reducers/index';
import { getProps } from 'utils/selectors';

type LeadId = MayBe<string> | null;

const getState = (state: RootState) => state.leads;

export const getUIState = createSelector(getState, ({ ui }) => ui);
const getDataState = createSelector(getState, ({ data }) => data);
const getAllLeads = createSelector(getDataState, data => data.entities);

export const getById = createSelector(
  getAllLeads,
  getProps<LeadId>,
  (leads, leadId): MayBe<StoredLead> => (leadId ? leads[leadId] : undefined)
);
export const getByIds = createSelector(
  getAllLeads,
  getProps<MayBe<Array<string>>>,
  (leads, leadIds): Array<MayBe<StoredLead>> => leadIds?.map(id => leads[id]) ?? []
);

const includesLead = (leadIds: Array<string>, leadId: LeadId): boolean => typeof leadId === 'string' && leadIds.includes(leadId);

const getLoadingByLead = createSelector(getUIState, ({ loadingByLead }) => loadingByLead);
export const isLeadLoading = createSelector(getLoadingByLead, getProps<LeadId>, (loadingByLead, leadId) =>
  Object.values(loadingByLead).some(leadIds => includesLead(leadIds, leadId)));
export const isAssigning = createSelector(getLoadingByLead, getProps<LeadId>, ({ assign }, leadId) => includesLead(assign, leadId));
export const isCreatingDirectMessage = createSelector(getLoadingByLead, getProps<LeadId>, ({ createDirectMessage }, leadId) =>
  includesLead(createDirectMessage, leadId));

export const canSetAssignee = createSelector(
  hasSalesManagingRole,
  getCurrentUserIsSalesman,
  getById,
  (isManager, isSalesman, lead) => isManager || (isSalesman && !lead?.assigneeId)
);

export const isCurrentUserLeadAssignable = createSelector(
  isCurrentUserSalesManager,
  getCurrentUserIsSalesman,
  getById,
  (isManager, isSalesman, lead) => isManager || (isSalesman && !lead?.assigneeId)
);
export const canCreateDirectMessage = createSelector(getById, lead => typeof lead?.directMessageId === 'undefined');
export const canDeleteLead = hasSalesManagingRole;
export const canMarkAsHandledLead = createSelector(getById, lead => lead?.status !== LeadStatusEnum.HANDLED);
